export const getLuma = (color: string): number => {
  const { r, g, b } = getColorObj(color);
  // per ITU-R BT.709
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const shouldUseDarkText = (color: string): boolean => {
  try {
    return getLuma(color) > 200;
  } catch (error) {
    return false;
  }
};

// Tests if two colors have matching rgb values.
// Having the same rgb values but not the opacity should still mean the colors are the equivalent.
export const areColorsEquivalent = (color1: string, color2: string): boolean => {
  const areValidColors = isValidColor(color1) && isValidColor(color2);
  return areValidColors ? getRgb(color1) === getRgb(color2) : false;
};

export const isValidColor = (color: string): boolean => {
  return !!color && (isValidRgb(color) || isValidRgba(color) || isValidHex(color));
};

export const isValidHex = (color: string): boolean => {
  // Not valid if no code supplied
  if (color == null) {
    return false;
  }

  // Add a hash if one isn't present
  if (color.charAt(0) !== "#") {
    color = `#${color}`;
  }

  // Not valid if less than three digits
  // @NOTE its 4 digits because of '#' symbol at the beginning.
  if (color.length < 4) {
    return false;
  }

  // Method from:
  // http://stackoverflow.com/questions/8027423/how-to-check-if-a-string-is-a-valid-hex-color-representation
  // HEX Code is either 3 or 6 letters so we check both.
  return color.match(/(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i) != null;
};

const isValidRgb = (color: string): boolean => {
  const regexMatch = color.match(/^rgb\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
  if (!regexMatch) {
    return false;
  }

  const rgbValues = [regexMatch[1], regexMatch[2], regexMatch[3]];
  return !rgbValues.find((value) => {
    const numberVal = parseInt(value, 10);
    return numberVal > 255 || numberVal < 0;
  });
};

const isValidRgba = (color: string): boolean => {
  const regexMatch = color.match(/^rgba\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d\.]+)\s*\)/i);
  if (!regexMatch) {
    return false;
  }

  const rgbValues = [regexMatch[1], regexMatch[2], regexMatch[3]];
  const opacity = parseFloat(regexMatch[4]);
  return (
    !rgbValues.find((value) => {
      const numberVal = parseInt(value, 10);
      return numberVal > 255 || numberVal < 0;
    }) &&
    opacity >= 0 &&
    opacity <= 1
  );
};

const convertTo6CharHex = (color: string): string => {
  if (color.length === 6) {
    return color;
  }

  // Remove hash if one is present
  if (color.charAt(0) === "#") {
    color = color.substring(1);
  }

  if (color.length === 3) {
    color += color;
  }

  return color.toLowerCase();
};

const getColorObj = (color: string) => {
  if (isValidHex(color)) {
    const hex = convertTo6CharHex(color);

    // Convert rrggbb to decimal
    const rgb = parseInt(hex, 16);

    return {
      /* tslint:disable:no-bitwise */
      r: (rgb >> 16) & 0xff,
      g: (rgb >> 8) & 0xff,
      b: (rgb >> 0) & 0xff,
      /* tslint:enable:no-bitwise */
      opacity: 1,
    };
  } else if (isValidRgb(color)) {
    const regexMatch = color.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*/) as string[];
    return {
      r: parseInt(regexMatch[1], 10),
      g: parseInt(regexMatch[2], 10),
      b: parseInt(regexMatch[3], 10),
      opacity: 1,
    };
  } else if (isValidRgba(color)) {
    const regexMatch = color.match(/^rgba\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d\.]+)\s*\)/i) as string[];
    const opacity = parseFloat(regexMatch[4]);
    return {
      r: parseInt(regexMatch[1], 10),
      g: parseInt(regexMatch[2], 10),
      b: parseInt(regexMatch[3], 10),
      opacity,
    };
  } else {
    throw new Error("Invalid color");
  }
};

export const getRgb = (color: string): string => {
  const { r, g, b } = getColorObj(color);
  return `rgb(${r}, ${g}, ${b})`;
};

export const getHex = (color: string): string => {
  const { r, g, b } = getColorObj(color);
  return `#${getHexUnit(r)}${getHexUnit(g)}${getHexUnit(b)}`;
};

export const getRgba = (color: string): string => {
  const { r, g, b, opacity } = getColorObj(color);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const getHexUnit = (c: number) => {
  const hex = c.toString(16);
  return hex.length == 1 ? `0${hex}` : hex;
};
